import { useEffect, useState } from "react";

import { handleSetAssignment } from "@/assignments/utils/assignmentActions";
import useExpirationWarning from "@/assignments/utils/useExpirationWarning";
import useHasFeature from "@/features/useHasFeature";
import useGetUserProperties from "@/users/v3/useGetUserProperties";
import AssignmentStudentPreviewToggle from "@/view/AssignmentStudentPreviewToggle";
import useAssignmentReducerContext from "@/view/utils/useAssignmentReducerContext";
import useStudentPreviewContext from "@/view/utils/useStudentPreviewContext";
import useSetAssignmentOnLoad from "../utils/useSetAssignmentOnLoad";
import {
  rootCss,
  wrappingContainerCss,
} from "./AssignmentEmbedContainer.style";
import AssignmentEmbedStudentContainer from "./AssignmentEmbedStudentContainer";

import type { Assignment, LangstonContentResponse } from "@/types/models";
import type ContentLevel from "@/types/models/ContentLevel.interface";

interface AssignmentEmbedContainerProps {
  content: LangstonContentResponse | null | undefined;
  currentContentLevel: ContentLevel | null | undefined;
  fetchAssignment: Function;
  hasMultipleAssignments: boolean;
  isSelectedAssignmentLoading: boolean;
  latestAssignment: Assignment | null | undefined;
  selectedAssignment: Assignment | null | undefined;
}

const AssignmentEmbedContainer = ({
  content,
  currentContentLevel,
  fetchAssignment,
  hasMultipleAssignments,
  isSelectedAssignmentLoading,
  latestAssignment,
  selectedAssignment,
}: AssignmentEmbedContainerProps) => {
  const { isStudent, isTeacher } = useGetUserProperties();
  const { setIsAssignmentLevelLockedForStudentPreview } =
    useStudentPreviewContext();
  const [hasStudentPreviewFeature] = useHasFeature("student_preview");
  const { updatedAssignment, dispatch } = useAssignmentReducerContext();

  // The embed should only be set as loading on first load of the assignment.
  // It should not show a loading state when refetching the assignment post-save.
  const [isLoading, setIsLoading] = useState(true);

  useSetAssignmentOnLoad({
    fetchAssignment,
    handleSetAssignment: (formattedAssignment: unknown) =>
      handleSetAssignment(formattedAssignment, dispatch),
    isSelectedAssignmentLoading,
    isStudent,
    latestAssignment,
    selectedAssignment,
    setIsLoading,
  });

  useExpirationWarning(content?.data);

  // Calculated variables:
  const currentContentLevelId = currentContentLevel?.id;

  useEffect(() => {
    setIsAssignmentLevelLockedForStudentPreview(
      updatedAssignment?.is_reading_level_locked,
    );
  }, [JSON.stringify(updatedAssignment)]);

  // TODO: Handle loading view here.
  if (isLoading) return null;

  return (
    <section css={[rootCss]}>
      <div css={wrappingContainerCss}>
        {hasStudentPreviewFeature && isTeacher && (
          <AssignmentStudentPreviewToggle
            currentContentLevel={currentContentLevel}
          />
        )}
        <AssignmentEmbedStudentContainer
          assignment={updatedAssignment}
          currentContentLevelId={currentContentLevelId}
          hasMultipleAssignments={hasMultipleAssignments}
        />
      </div>
    </section>
  );
};

export default AssignmentEmbedContainer;
